import mapboxgl from 'mapbox-gl';
import { logger } from '../../../utils/logger';

const log = logger('SpotWxHandler');

/**
 * Forecast models available in SpotWx
 * Same models as EasyMap 2 implementation
 */
const FORECAST_MODELS = [
  { model: 'hrdps_continental', name: 'HRDPS Continental 2 Day', flag: 'canada' },
  { model: 'rdps_10km', name: 'RDPS 10km 2 Day', flag: 'canada' },
  { model: 'gem_glb_15km', name: 'GDPS 16 Day', flag: 'canada' },
  { model: 'geps_0p5_raw', name: 'GEPS 0.5 Degree 16 Day', flag: 'canada' },
  { model: 'sref_pgrb132', name: 'SREF 87h', flag: 'usa' },
  { model: 'gfs_pgrb2_0p25_f', name: 'GFS 10 Day', flag: 'usa' },
  { model: 'gfs_uv', name: 'GFS UV 10 Day', flag: 'usa' },
];

/**
 * SpotWx Handler - handles map interaction for SpotWx forecast links
 * Click anywhere on map to show popup with forecast model links
 */
export class SpotWxHandler {
  private map: mapboxgl.Map;
  private enabled: boolean = false;
  private clickHandler: ((e: mapboxgl.MapMouseEvent) => void) | null = null;
  private currentPopup: mapboxgl.Popup | null = null;

  constructor(map: mapboxgl.Map) {
    this.map = map;
  }

  /**
   * Enable SpotWx mode - attach click handler and update cursor
   */
  public enable(): void {
    if (this.enabled) {
      return;
    }
    this.enabled = true;

    const canvas = this.map.getCanvas();
    const container = this.map.getContainer();

    // Use inline style with !important - CSS class approach fails due to component scoping
    canvas.style.setProperty('cursor', 'crosshair', 'important');
    container.style.setProperty('cursor', 'crosshair', 'important');

    // Attach click handler
    this.clickHandler = this.handleClick.bind(this);
    this.map.on('click', this.clickHandler);

    // Attach mousemove handler to maintain cursor
    this.map.on('mousemove', this.handleMouseMove);

    log.debug('SpotWx handler enabled');
  }

  /**
   * Disable SpotWx mode - remove click handler and reset cursor
   */
  public disable(): void {
    if (!this.enabled) {
      return;
    }
    this.enabled = false;

    const canvas = this.map.getCanvas();
    const container = this.map.getContainer();

    canvas.style.removeProperty('cursor');
    container.style.removeProperty('cursor');

    if (this.clickHandler) {
      this.map.off('click', this.clickHandler);
      this.clickHandler = null;
    }

    this.map.off('mousemove', this.handleMouseMove);

    if (this.currentPopup) {
      this.currentPopup.remove();
      this.currentPopup = null;
    }

    log.debug('SpotWx handler disabled');
  }

  /**
   * Check if handler is enabled
   */
  public isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Handle mouse move to restore cursor - other layers fight for cursor control
   */
  private handleMouseMove = (): void => {
    if (this.enabled) {
      const canvas = this.map.getCanvas();
      const container = this.map.getContainer();

      // Re-apply cursor in case Mapbox layer interactions changed it
      if (canvas.style.cursor !== 'crosshair') {
        canvas.style.setProperty('cursor', 'crosshair', 'important');
        container.style.setProperty('cursor', 'crosshair', 'important');
      }
    }
  };

  /**
   * Show forecast popup at a specific location — used by context menu
   */
  public showForecast(lngLat: mapboxgl.LngLat): void {
    const lat = Math.round(lngLat.lat * 100000) / 100000;
    const lon = Math.round(lngLat.lng * 100000) / 100000;
    this.showForecastPopup(lngLat, lat, lon);
  }

  /**
   * Handle map click - show popup with forecast links
   */
  private handleClick(e: mapboxgl.MapMouseEvent): void {
    log.debug('handleClick at', e.lngLat);

    // Round coordinates like EM2 does
    const lat = Math.round(e.lngLat.lat * 100000) / 100000;
    const lon = Math.round(e.lngLat.lng * 100000) / 100000;

    this.showForecastPopup(e.lngLat, lat, lon);
  }

  /**
   * Show popup with forecast model links
   */
  private showForecastPopup(lngLat: mapboxgl.LngLat, lat: number, lon: number): void {
    if (this.currentPopup) {
      this.currentPopup.remove();
    }

    const html = this.buildPopupContent(lat, lon);

    this.currentPopup = new mapboxgl.Popup({
      closeButton: true,
      closeOnClick: true,
      className: 'spotwx-popup',
      maxWidth: '320px'
    })
      .setLngLat(lngLat)
      .setHTML(html)
      .addTo(this.map);

    this.currentPopup.on('close', () => {
      this.currentPopup = null;
    });
  }

  /**
   * Build popup HTML content with forecast links
   */
  private buildPopupContent(lat: number, lon: number): string {
    const baseUrl = 'https://spotwx.com/products/grib_index.php?model=';
    const locationUrl = `&lat=${lat}&lon=${lon}&tz=America/Yellowknife&label=`;

    const links = FORECAST_MODELS.map(forecast => {
      const fullUrl = baseUrl + forecast.model + locationUrl;
      const flagIcon = forecast.flag === 'canada' ? '🍁' : '🇺🇸';
      return `
        <li class="spotwx-forecast-item">
          <a href="${fullUrl}" target="_blank" rel="noopener noreferrer">
            <span class="spotwx-flag">${flagIcon}</span>
            ${forecast.name}
          </a>
        </li>
      `;
    }).join('');

    return `
      <div class="spotwx-popup-content">
        <div class="spotwx-header">
          <img src="/images/spotwx_logo_transparent.png" alt="SpotWx" class="spotwx-logo" />
          <div class="spotwx-coords">${lat.toFixed(5)}, ${lon.toFixed(5)}</div>
        </div>
        <ul class="spotwx-forecast-list">
          ${links}
        </ul>
      </div>
    `;
  }

  /**
   * Clean up the handler
   */
  public destroy(): void {
    this.disable();
  }
}
